import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import GroupThreads from '@/components/GroupThreads'
import GroupComments from '@/components/GroupComments'
import EditGroupModal from '@/components/EditGroupModal'
import { useState, useEffect } from 'react'
import { useModerator } from '@/hooks/useModerator'

export default function GroupDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)

  // When navigated with state.selectedThreadId (e.g. after "Discuss" → add comment or new comment from TV), select that thread so comments are visible
  useEffect(() => {
    const state = location.state as { selectedThreadId?: string } | undefined
    if (id && state?.selectedThreadId) {
      setSelectedThreadId(state.selectedThreadId)
    }
  }, [id, location.state])
  const isModerator = useModerator()
  const queryClient = useQueryClient()

  const { data: group, isLoading } = useQuery({
    queryKey: ['group', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          description,
          owner_id,
          profiles!groups_owner_id_fkey (
            display_name
          )
        `)
        .eq('id', id)
        .single()

      if (error) throw error
      
      // Transform data: Supabase returns profiles as array, extract first element
      const profiles = Array.isArray(data.profiles)
        ? (data.profiles[0] || null)
        : data.profiles
      
      return {
        ...data,
        profiles,
        owner: profiles?.display_name || 'Unknown',
      }
    },
    enabled: !!id,
  })

  const deleteGroup = useMutation({
    mutationFn: async (groupId: string) => {
      // First, delete all comments in threads belonging to this group
      // Get all thread IDs for this group
      const { data: threads, error: threadsError } = await supabase
        .from('threads')
        .select('id')
        .eq('group_id', groupId)

      if (threadsError) throw threadsError

      // Delete all comments for these threads
      if (threads && threads.length > 0) {
        const threadIds = threads.map(t => t.id)
        const { error: commentsError } = await supabase
          .from('comments')
          .delete()
          .in('thread_id', threadIds)

        if (commentsError) throw commentsError
      }

      // Then delete all threads in this group
      const { error: threadsDeleteError } = await supabase
        .from('threads')
        .delete()
        .eq('group_id', groupId)

      if (threadsDeleteError) throw threadsDeleteError

      // Delete group memberships
      const { error: membersError } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)

      if (membersError) throw membersError

      // Finally, delete the group itself
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', groupId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['other-groups'] })
      queryClient.invalidateQueries({ queryKey: ['user-groups'] })
      queryClient.invalidateQueries({ queryKey: ['user-groups-sidebar'] })
      // Navigate back to groups page after deletion
      navigate('/groups')
    },
  })

  const handleEditGroup = () => {
    setShowEditModal(true)
  }

  const handleDeleteGroup = async () => {
    if (!id) return
    if (confirm('Are you sure you want to delete this group? This will delete all threads and comments in this group. This action cannot be undone.')) {
      try {
        await deleteGroup.mutateAsync(id)
      } catch (err: any) {
        alert(err.message || 'Failed to delete group')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <p style={{ color: 'var(--text-primary)' }}>Loading group...</p>
      </div>
    )
  }

  if (!group) {
    return (
      <div className="max-w-7xl mx-auto">
        <p style={{ color: 'var(--text-primary)' }}>Group not found.</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      <Link
        to="/groups"
        className="block mb-4"
        style={{ color: 'var(--text-link)' }}
      >
        ← Back to Groups
      </Link>

      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 
              className="text-3xl font-bold mb-2"
              style={{ color: 'var(--text-primary)' }}
            >
              {group.name}
            </h1>
            {group.description && (
              <p 
                className="text-lg"
                style={{ color: 'var(--text-primary)' }}
              >
                {group.description}
              </p>
            )}
          </div>
          {isModerator && (
            <div className="flex gap-2">
              <button
                onClick={handleEditGroup}
                className="px-4 py-2 font-semibold rounded"
                style={{
                  backgroundColor: 'var(--bg-interactive)',
                  color: 'var(--text-inverse)',
                }}
                aria-label="Edit group"
              >
                Edit Group
              </button>
              <button
                onClick={handleDeleteGroup}
                className="px-4 py-2 font-semibold rounded"
                style={{
                  backgroundColor: 'var(--color-error)',
                  color: 'var(--text-inverse)',
                }}
                aria-label="Delete group"
              >
                Delete Group
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Threads Column */}
        <div>
          <GroupThreads 
            groupId={id!} 
            onThreadSelect={setSelectedThreadId}
            selectedThreadId={selectedThreadId}
          />
        </div>

        {/* Comments Column */}
        <div>
          <GroupComments 
            threadId={selectedThreadId}
            groupId={id!}
          />
        </div>
      </div>

      {showEditModal && group && (
        <EditGroupModal
          groupId={group.id}
          currentName={group.name}
          currentDescription={group.description || null}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false)
          }}
        />
      )}
    </div>
  )
}

